import { useState, useEffect } from 'react';

interface EnhancedLoadingSpinnerProps {
  isLoading: boolean;
  timeout?: number;
  message?: string;
}

/**
 * EnhancedLoadingSpinner component that shows a refresh button after 
 * a specified timeout. This gives users the ability to manually refresh
 * if they encounter a stuck spinner.
 */
export function EnhancedLoadingSpinner({ 
  isLoading,
  timeout = 5000,
  message = 'Loading...'
}: EnhancedLoadingSpinnerProps) {
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (isLoading) {
      // Show refresh button after specified timeout
      timer = setTimeout(() => {
        setShowRefreshButton(true);
      }, timeout);
    } else {
      // Reset when loading is complete
      setShowRefreshButton(false);
    }
    
    return () => {
      clearTimeout(timer);
    };
  }, [isLoading, timeout]);
  
  if (!isLoading) {
    return null;
  }
  
  return (
    <div className="flex flex-col items-center justify-center py-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800 mb-2"></div>
      <p className="text-gray-600 text-sm mb-2">{message}</p>
      
      {showRefreshButton && (
        <div className="text-center mt-2">
          <p className="text-gray-500 text-xs mb-2">
            This is taking longer than expected...
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      )}
    </div>
  );
}
